import { User, Tenant } from '../types/user';

export interface ImpersonationSession {
  adminUser: User;
  adminTenant: Tenant;
  impersonatedUser: User;
  impersonatedTenant: Tenant;
  startedAt: Date;
}

class ImpersonationService {
  private readonly STORAGE_KEY = 'impersonation_session';
  private readonly ADMIN_STORAGE_KEY = 'admin_original_state';

  /**
   * Inicia uma sessão de impersonation
   */
  startImpersonation(
    adminUser: User,
    adminTenant: Tenant,
    targetUser: User,
    targetTenant: Tenant
  ): boolean {
    try {
      // Salvar estado original do admin
      const adminState = {
        user: adminUser,
        tenant: adminTenant,
        timestamp: new Date().toISOString()
      };
      
      localStorage.setItem(this.ADMIN_STORAGE_KEY, JSON.stringify(adminState));

      // Criar sessão de impersonation
      const session: ImpersonationSession = {
        adminUser,
        adminTenant,
        impersonatedUser: targetUser,
        impersonatedTenant: targetTenant,
        startedAt: new Date()
      };

      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(session));

      // Log de auditoria
      this.logImpersonation(adminUser, targetUser, targetTenant, 'start');

      console.log('🎭 Impersonation iniciada:', {
        admin: adminUser.email,
        target: targetUser.email,
        tenant: targetTenant.name
      });

      return true;
    } catch (error) {
      console.error('Erro ao iniciar impersonation:', error);
      return false;
    }
  }

  /**
   * Termina a sessão de impersonation e retorna ao admin
   */
  endImpersonation(): { user: User; tenant: Tenant } | null {
    try {
      const session = this.getActiveSession();
      
      if (!session) {
        console.warn('Nenhuma sessão de impersonation ativa');
        return null;
      }

      // Log de auditoria
      this.logImpersonation(
        session.adminUser,
        session.impersonatedUser,
        session.impersonatedTenant,
        'end'
      );

      // Recuperar estado original do admin
      const adminStateStr = localStorage.getItem(this.ADMIN_STORAGE_KEY);
      if (!adminStateStr) {
        console.error('Estado original do admin não encontrado');
        return null;
      }

      const adminState = JSON.parse(adminStateStr);

      // Limpar sessão de impersonation
      sessionStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem(this.ADMIN_STORAGE_KEY);

      console.log('🎭 Impersonation encerrada, retornando ao admin');

      return {
        user: adminState.user,
        tenant: adminState.tenant
      };
    } catch (error) {
      console.error('Erro ao encerrar impersonation:', error);
      return null;
    }
  }

  /**
   * Verifica se existe uma sessão de impersonation ativa
   */
  isImpersonating(): boolean {
    return sessionStorage.getItem(this.STORAGE_KEY) !== null;
  }

  /**
   * Obtém a sessão de impersonation ativa
   */
  getActiveSession(): ImpersonationSession | null {
    try {
      const sessionStr = sessionStorage.getItem(this.STORAGE_KEY);
      if (!sessionStr) return null;

      const session = JSON.parse(sessionStr);
      return {
        ...session,
        startedAt: new Date(session.startedAt)
      };
    } catch (error) {
      console.error('Erro ao recuperar sessão de impersonation:', error);
      return null;
    }
  }

  /**
   * Obtém informações do admin original
   */
  getAdminInfo(): { user: User; tenant: Tenant } | null {
    try {
      const adminStateStr = localStorage.getItem(this.ADMIN_STORAGE_KEY);
      if (!adminStateStr) return null;

      const adminState = JSON.parse(adminStateStr);
      return {
        user: adminState.user,
        tenant: adminState.tenant
      };
    } catch (error) {
      console.error('Erro ao recuperar info do admin:', error);
      return null;
    }
  }

  /**
   * Registra ação de impersonation para auditoria
   */
  private logImpersonation(
    adminUser: User,
    targetUser: User,
    targetTenant: Tenant,
    action: 'start' | 'end'
  ): void {
    try {
      const logEntry = {
        timestamp: new Date().toISOString(),
        action,
        adminUserId: adminUser.id,
        adminEmail: adminUser.email,
        targetUserId: targetUser.id,
        targetEmail: targetUser.email,
        targetTenantId: targetTenant.id,
        targetTenantName: targetTenant.name,
        userAgent: navigator.userAgent
      };

      // Armazenar em localStorage para persistência (em produção, enviar para backend)
      const logsKey = 'impersonation_audit_logs';
      const existingLogsStr = localStorage.getItem(logsKey);
      const existingLogs = existingLogsStr ? JSON.parse(existingLogsStr) : [];
      
      existingLogs.push(logEntry);
      
      // Manter apenas os últimos 100 logs
      if (existingLogs.length > 100) {
        existingLogs.shift();
      }
      
      localStorage.setItem(logsKey, JSON.stringify(existingLogs));

      console.log('📋 Log de impersonation registrado:', logEntry);
    } catch (error) {
      console.error('Erro ao registrar log de impersonation:', error);
    }
  }

  /**
   * Obtém logs de auditoria de impersonation
   */
  getAuditLogs(): any[] {
    try {
      const logsKey = 'impersonation_audit_logs';
      const logsStr = localStorage.getItem(logsKey);
      return logsStr ? JSON.parse(logsStr) : [];
    } catch (error) {
      console.error('Erro ao recuperar logs de auditoria:', error);
      return [];
    }
  }

  /**
   * Limpa sessão de impersonation (útil em caso de erro)
   */
  clearImpersonation(): void {
    sessionStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.ADMIN_STORAGE_KEY);
    console.log('🧹 Sessão de impersonation limpa');
  }
}

export const impersonationService = new ImpersonationService();

